const HTML_PATTERN = /<!doctype\s+html|<([a-z][a-z0-9-]*)(?:\s[^>]*)?>/i;

const ALLOWED_TAGS = new Set([
  "A", "BLOCKQUOTE", "BR", "DIV", "H1", "H2", "H3", "H4", "H5", "H6",
  "LI", "OL", "P", "SPAN", "TABLE", "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "UL",
]);

const ALLOWED_STYLES = new Set([
  "background-color", "border", "border-collapse", "color", "font-family", "font-size",
  "font-style", "font-weight", "margin", "margin-bottom", "margin-left", "margin-right",
  "margin-top", "min-width", "padding", "padding-bottom", "padding-left", "padding-right",
  "padding-top", "text-align", "text-decoration", "vertical-align", "width",
]);

const FONT_SIZE_MAP = {
  "1": "0.75rem",
  "2": "0.875rem",
  "3": "1rem",
  "4": "1.125rem",
  "5": "1.35rem",
  "6": "1.65rem",
  "7": "2rem",
};

const escapeHtml = (text) => String(text || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const replaceWithStyledSpan = (document, element, styles) => {
  const span = document.createElement("span");
  Object.entries(styles).forEach(([property, value]) => {
    if (value) span.style[property] = value;
  });
  while (element.firstChild) span.appendChild(element.firstChild);
  element.replaceWith(span);
};

const cleanStyle = (element) => {
  const safeDeclarations = [];
  Array.from({ length: element.style.length }, (_, index) => element.style.item(index)).forEach((property) => {
    if (!ALLOWED_STYLES.has(property)) return;
    const value = element.style.getPropertyValue(property).trim();
    if (!value || /url\s*\(|expression\s*\(|javascript:/i.test(value)) return;
    safeDeclarations.push(`${property}: ${value}`);
  });
  if (safeDeclarations.length > 0) {
    element.setAttribute("style", `${safeDeclarations.join("; ")};`);
  } else {
    element.removeAttribute("style");
  }
};

export const containsRichTextHtml = (value) => HTML_PATTERN.test(String(value || ""));

export const prepareRichTextForEditing = (value) => {
  if (containsRichTextHtml(value)) return normalizeRichTextHtml(value);
  return escapeHtml(value).replace(/\r?\n/g, "<br>");
};

export const normalizeRichTextHtml = (html) => {
  if (typeof DOMParser === "undefined") return String(html || "");

  const document = new DOMParser().parseFromString(String(html || ""), "text/html");
  document.body.querySelectorAll("script, style, iframe, object, embed, form, input, button, textarea, select")
    .forEach((element) => element.remove());

  document.body.querySelectorAll("b, strong").forEach((element) =>
    replaceWithStyledSpan(document, element, { fontWeight: "700" }));
  document.body.querySelectorAll("i, em").forEach((element) =>
    replaceWithStyledSpan(document, element, { fontStyle: "italic" }));
  document.body.querySelectorAll("u").forEach((element) =>
    replaceWithStyledSpan(document, element, { textDecoration: "underline" }));
  document.body.querySelectorAll("s, strike").forEach((element) =>
    replaceWithStyledSpan(document, element, { textDecoration: "line-through" }));
  document.body.querySelectorAll("big").forEach((element) =>
    replaceWithStyledSpan(document, element, { fontSize: "1.2em" }));
  document.body.querySelectorAll("small").forEach((element) =>
    replaceWithStyledSpan(document, element, { fontSize: "0.85em" }));
  document.body.querySelectorAll("font").forEach((element) =>
    replaceWithStyledSpan(document, element, {
      color: element.getAttribute("color"),
      fontFamily: element.getAttribute("face"),
      fontSize: FONT_SIZE_MAP[element.getAttribute("size")] || "",
    }));

  [...document.body.querySelectorAll("*")].forEach((element) => {
    if (!ALLOWED_TAGS.has(element.tagName)) {
      element.replaceWith(...element.childNodes);
      return;
    }

    [...element.attributes].forEach((attribute) => {
      if (!(["href", "style", "target", "rel"].includes(attribute.name.toLowerCase()))) {
        element.removeAttribute(attribute.name);
      }
    });
    cleanStyle(element);

    if (element.tagName === "A") {
      const href = element.getAttribute("href") || "";
      if (!/^(https?:|mailto:|tel:|#)/i.test(href)) element.removeAttribute("href");
      element.setAttribute("target", "_blank");
      element.setAttribute("rel", "noopener noreferrer");
      if (!element.style.color) element.style.color = "#1565c0";
      if (!element.style.textDecoration) element.style.textDecoration = "underline";
    } else {
      element.removeAttribute("target");
      element.removeAttribute("rel");
      element.removeAttribute("href");
    }
  });

  return document.body.innerHTML.trim();
};

export const richTextToPlainText = (element) => (element?.innerText || "")
  .replace(/\u00a0/g, " ")
  .replace(/\n{3,}/g, "\n\n")
  .trim();

export const richTextValueToPlainText = (value) => {
  const source = String(value || "");
  if (!containsRichTextHtml(source)) return source;
  if (typeof DOMParser === "undefined") return source.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const document = new DOMParser().parseFromString(source, "text/html");
  document.body.querySelectorAll("script, style, noscript, template").forEach((element) => element.remove());
  document.body.querySelectorAll("br").forEach((element) => element.replaceWith("\n"));
  document.body.querySelectorAll("li").forEach((element) => {
    element.prepend("• ");
    element.append("\n");
  });
  document.body.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, blockquote, tr")
    .forEach((element) => element.append("\n"));

  return (document.body.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const buildInlineTable = (rowCount, columnCount) => {
  const rows = Math.max(1, Math.min(Number(rowCount) || 2, 10));
  const columns = Math.max(1, Math.min(Number(columnCount) || 2, 8));
  const cells = Array.from({ length: columns }, (_, index) =>
    `<td style="border: 1px solid #808080; min-width: 80px; padding: 8px;">Column ${index + 1}</td>`
  ).join("");
  const body = Array.from({ length: rows }, () => `<tr>${cells}</tr>`).join("");
  return `<table style="border-collapse: collapse; margin: 12px 0; width: 100%;"><tbody>${body}</tbody></table><div><br></div>`;
};

export { escapeHtml };
